import React, { useState, useEffect } from 'react';
import './ClassManagementFormOffline.scss';
import { postApi } from '../../../Utlis/apiService';
import { CREATE_OFFLINE_CLASS_PRICE, UPDATE_OFFLINE_CLASS_PRICE } from '../../../Utlis/apiPath';
import { errorToast, successToast } from '../../../Services/ToastHelper';
import Loader from '../../../Components/Loader/Loader';

const ClassManagementFormOffline = ({ setAddOfflineClassForm, fetchAllOfflineClasses, editData, tableData }) => {
    const [customPerClassPrice, setCustomPerClassPrice] = useState({
        price: '',
        compareAtPrice: '',
    });
    const [alternateMonthlyPrice, setAlternateMonthlyPrice] = useState({
        price: '',
        compareAtPrice: '',
    });
    const [isLoading, setIsLoading] = useState(false);
    const [original, setOriginal] = useState(null);

    // Properly prefill form based on table data with specific titles
    useEffect(() => {
        if (editData && tableData && tableData.length > 0) {
            // Find the custom pricing row (title contains "custom")
            const customRow = tableData.find(row =>
                row.title && row.title.toLowerCase().includes('custom')
            );

            // Find the monthly/alternate pricing row (title contains "monthly" or "alternate")
            const monthlyRow = tableData.find(row =>
                row.title && (
                    row.title.toLowerCase().includes('monthly') ||
                    row.title.toLowerCase().includes('alternate')
                )
            );

            // Set custom per class pricing
            const customData = {
                price: customRow?.price || '',
                compareAtPrice: customRow?.compareAtPrice || '',
            };

            // Set alternate monthly pricing
            const monthlyData = {
                price: monthlyRow?.price || '',
                compareAtPrice: monthlyRow?.compareAtPrice || '',
            };

            setCustomPerClassPrice(customData);
            setAlternateMonthlyPrice(monthlyData);

            // Store original values for change detection
            setOriginal({
                customPrice: customData.price,
                customCompare: customData.compareAtPrice,
                monthlyPrice: monthlyData.price,
                monthlyCompare: monthlyData.compareAtPrice,
                customId: customRow?.key,
                monthlyId: monthlyRow?.key,
            });
        } else {
            resetForm();
        }
    }, [editData, tableData]);

    const handleCustomPerClassChange = (e) => {
        const { name, value } = e.target;
        setCustomPerClassPrice((prev) => ({ ...prev, [name]: value }));
    };

    const handleAlternateMonthlyChange = (e) => {
        const { name, value } = e.target;
        setAlternateMonthlyPrice((prev) => ({ ...prev, [name]: value }));
    };

    const validateForm = () => {
        if (!customPerClassPrice.price || !customPerClassPrice.compareAtPrice) {
            errorToast('Custom per class price and compare-at price are required');
            return false;
        }
        if (!alternateMonthlyPrice.price || !alternateMonthlyPrice.compareAtPrice) {
            errorToast('Alternate monthly price and compare-at price are required');
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        // Prepare data according to API structure from the image
        const requestData = editData && original ? {
            // For edit mode: send changed values or previous values (not null)
            alternateMonthlyPrice:
                String(alternateMonthlyPrice.price) !== String(original.monthlyPrice)
                    ? parseFloat(alternateMonthlyPrice.price)
                    : parseFloat(original.monthlyPrice), // Send previous value instead of null
            customPerClassPrice:
                String(customPerClassPrice.price) !== String(original.customPrice)
                    ? parseFloat(customPerClassPrice.price)
                    : parseFloat(original.customPrice), // Send previous value instead of null
            compareAlternateMonthlyPrice:
                String(alternateMonthlyPrice.compareAtPrice) !== String(original.monthlyCompare)
                    ? parseFloat(alternateMonthlyPrice.compareAtPrice)
                    : parseFloat(original.monthlyCompare), // Send previous value instead of null
            compareCustomPerClassPrice:
                String(customPerClassPrice.compareAtPrice) !== String(original.customCompare)
                    ? parseFloat(customPerClassPrice.compareAtPrice)
                    : parseFloat(original.customCompare), // Send previous value instead of null
        } : {
            // For new entries, send all values
            alternateMonthlyPrice: parseFloat(alternateMonthlyPrice.price),
            customPerClassPrice: parseFloat(customPerClassPrice.price),
            compareAlternateMonthlyPrice: parseFloat(alternateMonthlyPrice.compareAtPrice),
            compareCustomPerClassPrice: parseFloat(customPerClassPrice.compareAtPrice),
        };

        setIsLoading(true);

        try {
            let response;

            // Both create and update use POST method
            if (editData && original) {
                // Update existing offline class prices - still use POST
                response = await postApi(UPDATE_OFFLINE_CLASS_PRICE, requestData);
            } else {
                // Create new offline class prices - use POST
                response = await postApi(CREATE_OFFLINE_CLASS_PRICE, requestData);
            }

            const { status, message: responseMessage } = response;

            if (status === 200 || status === 201) {
                successToast(responseMessage || `Offline class pricing ${editData ? 'updated' : 'created'} successfully`);
                setAddOfflineClassForm(false);
                fetchAllOfflineClasses();
                resetForm();
            } else {
                errorToast(responseMessage || `Failed to ${editData ? 'update' : 'create'} offline class pricing`);
            }
        } catch (error) {
            console.error(`Error ${editData ? 'updating' : 'creating'} offline class pricing:`, error);
            errorToast(`An error occurred while ${editData ? 'updating' : 'creating'} the offline class pricing`);
        } finally {
            setIsLoading(false);
        }
    };


    const resetForm = () => {
        setCustomPerClassPrice({ price: '', compareAtPrice: '' });
        setAlternateMonthlyPrice({ price: '', compareAtPrice: '' });
        setOriginal(null);
    };

    const handleCancel = () => {
        setAddOfflineClassForm(false);
        resetForm();
    };

    return (
        <div className="offline-pricing-form">
            {isLoading && <Loader />}
            <div className="form-container">
                <form onSubmit={handleSubmit}>
                    {/* Custom Per Class Pricing Section */}
                    <div className="form-section custom-per-class">
                        <h2 className="section-title">Pricing (Custom Per Class Price)</h2>
                        <div className="input-grid">
                            <div className="input-group">
                                <label htmlFor="customPerClassPrice-price" className="input-label">
                                    Price*
                                </label>
                                <input
                                    type="number"
                                    id="customPerClassPrice-price"
                                    name="price"
                                    value={customPerClassPrice.price}
                                    onChange={handleCustomPerClassChange}
                                    placeholder="0.00"
                                    className="form-input"
                                    step="0.01"
                                    min="0"
                                    required
                                />
                            </div>

                            <div className="input-group">
                                <label htmlFor="customPerClassPrice-compareAtPrice" className="input-label">
                                    Compare-at Price*
                                </label>
                                <input
                                    type="number"
                                    id="customPerClassPrice-compareAtPrice"
                                    name="compareAtPrice"
                                    value={customPerClassPrice.compareAtPrice}
                                    onChange={handleCustomPerClassChange}
                                    placeholder="0.00"
                                    className="form-input"
                                    step="0.01"
                                    min="0"
                                    required
                                />
                            </div>
                        </div>
                    </div>

                    {/* Alternate Monthly Price Section */}
                    <div className="form-section alternate-monthly">
                        <h2 className="section-title">Pricing (Alternate Monthly Price)</h2>
                        <div className="input-grid">
                            <div className="input-group">
                                <label htmlFor="alternateMonthlyPrice-price" className="input-label">
                                    Price*
                                </label>
                                <input
                                    type="number"
                                    id="alternateMonthlyPrice-price"
                                    name="price"
                                    value={alternateMonthlyPrice.price}
                                    onChange={handleAlternateMonthlyChange}
                                    placeholder="0.00"
                                    className="form-input"
                                    step="0.01"
                                    min="0"
                                    required
                                />
                            </div>

                            <div className="input-group">
                                <label htmlFor="alternateMonthlyPrice-compareAtPrice" className="input-label">
                                    Compare-at Price*
                                </label>
                                <input
                                    type="number"
                                    id="alternateMonthlyPrice-compareAtPrice"
                                    name="compareAtPrice"
                                    value={alternateMonthlyPrice.compareAtPrice}
                                    onChange={handleAlternateMonthlyChange}
                                    placeholder="0.00"
                                    className="form-input"
                                    step="0.01"
                                    min="0"
                                    required
                                />
                            </div>
                        </div>
                    </div>

                    {/* Form Actions */}
                    <div className="form-actions">
                        <button
                            type="button"
                            onClick={handleCancel}
                            className="cancel-btn"
                            disabled={isLoading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="submit-btn"
                            disabled={isLoading}
                        >
                            {isLoading ? 'Saving...' : (editData ? 'Update' : 'Save')} Pricing
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ClassManagementFormOffline;
